import { useEffect, useMemo, useState } from 'react';
import { WIDGET_GRID_HEIGHT } from '@/constants/grid';
import {
  GRID_COLUMN_WIDTH_MEDIUM,
  GRID_COLUMN_WIDTH_NARROW,
} from '@/constants/dimensions';
import { useOutletContext } from 'react-router';
import { useControlledDates } from '@/hooks/useControlledDates';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import {
  useGetBrokerageTransactionsQuery,
  useGetBrokerageTransactionsByAccountQuery,
} from '@/store/transactionsApi';
import MoveToCashFlowModal from '@/modules/cashFlow/MoveToCashFlowModal';
import {
  Box,
  IconButton,
  Typography,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { EmbeddableMenu } from '@/integrations/EmbeddableMenu';
import { useTheme } from '@mui/material/styles';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridToolbarProps,
  type GridListViewColDef,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useLocalization } from '@/hooks/useLocalization';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAutoResizeColumns } from '@/hooks/useAutoResizeColumns';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { Capacitor } from '@capacitor/core';
import { useTickerUrlFormatter } from '@/hooks/useTickerUrlFormatter';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { useManagedColumnVisibility } from '@/hooks/useManagedColumnVisibility';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';
import useAccountDetailsFlyout from '@/hooks/useAccountDetailsFlyout';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate, parseOptionalDate } from '@/utils/date';
import type { RecordResponse } from '@/models/ServiceTypes';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';
import { ContentContainer, ContentContainerVariant } from '../contentContainer';
import { GridContainer } from '@/components/grids/GridContainer';

interface InvestmentActivityProps extends StandardWidgetProps {
  accountId?: number;
  investorId?: number;
  showDateRange?: boolean;
  outletSetShowDateRange?: (show: boolean) => void;
  container?: ContentContainerVariant;
}

// Define a default date range (e.g., Year to Date)
const { startDate: initialStartDate, endDate: initialEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

type BrokerageTransactionRow = {
  WealthAccountTransactionId: number;
  WealthAccountId: number;
  WealthAccountName?: string | null;
  InstitutionName?: string | null;
  InstitutionId: number;
  CategoryName?: string | null;
  Description?: string | null;
  Amount: number;
  Quantity: number;
  TransactionDate?: string | null;
  TypeManuallyChanged: boolean;
  TypeChangedByRule: boolean;
  TradeDate?: string | null;
  SettlementDate?: string | null;
  CUSIP?: string | null;
  Symbol?: string | null;
  PrincipalCashAmount?: number;
  IncomeCashAmount?: number;
};

const getDisplayText = (value: string | number | boolean | null | undefined) =>
  typeof value === 'string' ? value.trim() : '';

/**
 * Trading activity: the page's grid, a dashboard widget, or one account's
 * transactions in the account flyout.
 *
 * On the Trading Activity page the range comes from the page header through
 * the route's outlet context, and the page is told to show its range picker.
 * In the account flyout (`container='flyout'`) the flyout picks its own range,
 * so the outlet context is ignored even though the flyout opens inside a page
 * that has one: the page header's dates would otherwise override the flyout's,
 * and opening the flyout would switch the page's picker.
 */
const InvestmentActivity = ({
  isWidget = false, // Defaulting to false, adjust if needed
  accountId,
  investorId,
  startDate: propStartDate,
  endDate: propEndDate,
  container = 'page',
}: InvestmentActivityProps) => {
  // Handle potential context if needed (e.g., for shared date range across a page)
  const outletContext =
    useOutletContext<Partial<InvestmentActivityProps> | null>();
  const pageContext = container === 'flyout' ? null : outletContext;
  const outletStartDate = pageContext?.startDate;
  const outletEndDate = pageContext?.endDate;
  const outletSetShowDateRange = pageContext?.outletSetShowDateRange;
  const effectiveExclusions = useEffectiveExclusions();
  const apiRef = useGridApiRef(); // Initialize the apiRef
  const { t } = useLocalization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] =
    useState<BrokerageTransactionRow | null>(null);
  const [optimisticallyHiddenRowIds, setOptimisticallyHiddenRowIds] = useState(
    new Set<string>(),
  ); // Added for optimistic hiding
  const [moveToCashFlowModalOpen, setMoveToCashFlowModalOpen] = useState(false);
  const [moveToCashFlowTx, setMoveToCashFlowTx] =
    useState<BrokerageTransactionRow | null>(null);
  const isNative = Capacitor.getPlatform() !== 'web';
  const theme = useTheme();
  // NOTE: in our theme `sm` is 800px.
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { ref: widgetWidthRef, isBelow: isWidgetBelowXsWidth } =
    useWidgetWidthIsBelow(isWidget, theme.breakpoints.values.sm);
  const shouldUseListView = isXs || (isWidget && isWidgetBelowXsWidth);

  const [hasAppliedXsColumnDefaults, setHasAppliedXsColumnDefaults] =
    useState(false);

  const {
    columnVisibilityModel,
    handleColumnVisibilityChange,
    applyColumnVisibility,
    defaultColumnVisibilityModel,
  } = useManagedColumnVisibility<BrokerageTransactionRow>({
    hideDisabledColumnMenu: true,
  });

  // EUD-440: clicking an account name opens the Account Details flyout.
  const { openAccountById, canOpenAccount, accountFlyout } =
    useAccountDetailsFlyout({ investorId });

  // On XS list view, default-hide Ticker + CUSIP (but don't override if the user already set them).
  useEffect(() => {
    if (!isXs || hasAppliedXsColumnDefaults) return;
    const prev = columnVisibilityModel;

    handleColumnVisibilityChange({
      ...prev,
      Symbol: 'Symbol' in prev ? prev.Symbol : false,
      CUSIP: 'CUSIP' in prev ? prev.CUSIP : false,
      PrincipalCashAmount:
        'PrincipalCashAmount' in prev ? prev.PrincipalCashAmount : false,
      IncomeCashAmount:
        'IncomeCashAmount' in prev ? prev.IncomeCashAmount : false,
    });
    setHasAppliedXsColumnDefaults(true);
  }, [
    isXs,
    hasAppliedXsColumnDefaults,
    columnVisibilityModel,
    handleColumnVisibilityChange,
  ]);

  const { tickerUrlFormatter } = useTickerUrlFormatter();

  const { startDate, endDate: controlledEndDate } = useControlledDates({
    propStartDate: outletStartDate ? outletStartDate : propStartDate,
    propEndDate: outletEndDate ? outletEndDate : propEndDate,
    isWidget,
    initialWidgetStartDate: initialStartDate, // Default start date for historical view
    initialWidgetEndDate: initialEndDate, // Default end date for historical view
  });

  // Inform parent/outlet to show date range while this component is mounted (only when not a widget)
  useEffect(() => {
    if (isWidget) return;
    outletSetShowDateRange?.(true);
    return () => {
      outletSetShowDateRange?.(false);
    };
  }, [isWidget, outletSetShowDateRange]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: BrokerageTransactionRow,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleMoveToCashFlow = () => {
    if (!selectedRow) return;
    setMoveToCashFlowTx(selectedRow); // preserve before menu clears it
    setMoveToCashFlowModalOpen(true);
    handleMenuClose();
  };

  // The backend interpolates these dates directly into the stored-procedure
  // EXEC; empty/undefined dates produce `@startDate=''`, which SQL cannot
  // convert to a date and returns HTTP 500. Skip the query until we have a
  // resolved range (EUD-587).
  const hasValidDateRange = Boolean(startDate && controlledEndDate);

  // Conditionally fetch data based on whether an accountId is provided
  const allTransactionsQuery = useGetBrokerageTransactionsQuery(
    {
      startDate: startDate || '',
      endDate: controlledEndDate || '',
      // Scope the rows to the same investor as the account-flyout lookup
      // (useAccountDetailsFlyout({ investorId })); otherwise advisor/client views
      // fetch unscoped rows whose accounts aren't in the scoped lookup, leaving
      // their account-name links disabled (EUD-440 review).
      ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
      ...(effectiveExclusions.excludedAccounts.length > 0
        ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
        : {}),
      ...(effectiveExclusions.excludedAccountTypes.length > 0
        ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
        : {}),
      ...(effectiveExclusions.excludedCategories.length > 0
        ? { ExcludedCategories: effectiveExclusions.excludedCategories }
        : {}),
    },
    { skip: !!accountId || !hasValidDateRange }, // Skip if accountId is present or dates are unresolved
  );

  const byAccountQuery = useGetBrokerageTransactionsByAccountQuery(
    {
      accountId: accountId ?? 0,
      startDate: startDate || '',
      endDate: controlledEndDate || '',
      ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
    },
    { skip: !accountId || !hasValidDateRange }, // Skip if no accountId or dates are unresolved
  );

  // Selecting between the two query results via a ternary widens the union to
  // `any` at this call site; read fields off the typed result object instead.
  const activeQuery: typeof allTransactionsQuery = accountId
    ? byAccountQuery
    : allTransactionsQuery;
  const { error } = activeQuery;
  const isLoading = Boolean(activeQuery.isLoading);
  const isFetching = Boolean(activeQuery.isFetching);
  const data = activeQuery.data as
    | RecordResponse<BrokerageTransactionRow>
    | undefined;

  // Define DataGrid columns based on BrokerageTransaction fields
  const rawColumns: GridColDef<BrokerageTransactionRow>[] = useMemo(() => {
    return [
      {
        field: 'TransactionDate',
        headerName: t('transactionDate', 'Transaction Date'),
        type: 'date',
        width: GRID_COLUMN_WIDTH_MEDIUM,
        valueGetter: (value) => parseOptionalDate(value),
        valueFormatter: (value) => formatAppDate(value),
        sortable: true,
      },
      {
        field: 'WealthAccountName',
        headerName: t('grid.accountName', 'Account'),
        flex: 1,
        minWidth: 180,
        sortable: true,
        // EUD-440: clicking the account name opens the Account Details flyout.
        renderCell: (params: GridRenderCellParams<BrokerageTransactionRow>) => {
          const name = params.row.WealthAccountName ?? '';
          if (!name) return null;
          if (!canOpenAccount(params.row.WealthAccountId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openAccountById(params.row.WealthAccountId);
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'TradeDate',
        headerName: t('grid.tradeDate', 'Trade Date'),
        type: 'date',
        width: GRID_COLUMN_WIDTH_NARROW,
        valueGetter: (value) => parseOptionalDate(value),
        valueFormatter: (value) => formatAppDate(value),
        sortable: true,
      },
      {
        field: 'SettlementDate',
        headerName: t('grid.settlementDate', 'Settlement Date'),
        type: 'date',
        width: GRID_COLUMN_WIDTH_NARROW,
        valueGetter: (value) => parseOptionalDate(value),
        valueFormatter: (value) => formatAppDate(value),
        sortable: true,
      },

      {
        field: 'Symbol',
        headerName: t('grid.ticker', 'Ticker'),
        // flex: 1,
        sortable: true,
        renderCell: (params) => {
          return tickerUrlFormatter(params.value);
        },
      },
      {
        field: 'CUSIP',
        headerName: t('grid.cusip', 'CUSIP'),
        // flex: 1,
        sortable: true,
      },
      {
        field: 'CategoryName',
        headerName: t('grid.type'),
        sortable: true,
      },
      {
        field: 'Description',
        headerName: t('description'),
        flex: 1,
        // minWidth: 200,
        sortable: false,
      },
      {
        field: 'Amount',
        headerName: t('grid.amount'),
        type: 'number',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatNumberToCurrency(value),
        sortable: true,
      },
      {
        field: 'PrincipalCashAmount',
        headerName: t('grid.principalCashAmount'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null | undefined) =>
          value !== null && value !== undefined
            ? formatNumberToCurrency(value)
            : '',
        sortable: true,
      },
      {
        field: 'IncomeCashAmount',
        headerName: t('grid.incomeCashAmount'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null | undefined) =>
          value !== null && value !== undefined
            ? formatNumberToCurrency(value)
            : '',
        sortable: true,
      },
      {
        field: 'actions',
        ...actionColumnConfig,
        width: 70,
        renderCell: (params) => (
          <IconButton
            size='small'
            onClick={(event) => {
              event.stopPropagation();
              handleMenuOpen(event, params.row);
            }}
          >
            <MoreVertIcon fontSize='small' />
          </IconButton>
        ),
      },
    ];
  }, [t, tickerUrlFormatter, canOpenAccount, openAccountById]);

  const columns = useMemo(() => {
    return applyColumnVisibility(rawColumns);
  }, [rawColumns, applyColumnVisibility]);

  // Auto-resize Account Name column when columns/data are loaded
  useAutoResizeColumns({
    apiRef,
    columns,
    enabled: !isXs,
    deps: [data],
    preferredField: 'WealthAccountName',
  });

  const listViewColDef: GridListViewColDef<BrokerageTransactionRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params: GridRenderCellParams<BrokerageTransactionRow>) => {
        const { row, api } = params;
        const visibilityModel = api.state.columns.columnVisibilityModel;
        const isFieldVisible = (field: string) =>
          !(field in visibilityModel) || visibilityModel[field];

        const description = getDisplayText(row.Description);
        const amount = formatNumberToCurrency(row.Amount);

        const typeValue = getDisplayText(row.CategoryName);

        const transactionDateValue = formatAppDate(row.TransactionDate);
        const tradeDateValue = formatAppDate(row.TradeDate);
        const settlementDateValue = formatAppDate(row.SettlementDate);

        const accountNameValue = getDisplayText(row.WealthAccountName);
        const tickerValue = getDisplayText(row.Symbol);
        const cusipValue = getDisplayText(row.CUSIP);

        const Line = (line: {
          left: string;
          right?: string;
          boldRight?: boolean;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
            >
              {line.left}
            </Typography>
            {line.right != null ? (
              <Typography
                variant='body2'
                sx={{
                  minWidth: 0,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                  ...(line.boldRight ? { fontWeight: '600 !important' } : {}),
                }}
              >
                {line.right}
              </Typography>
            ) : null}
          </Box>
        );

        return (
          <Box
            sx={{
              width: '100%',
              px: 0,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {/* Line 1: Description + Amount */}
            {(isFieldVisible('Description') || isFieldVisible('Amount')) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography
                  variant='body1'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: '600 !important',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                  }}
                >
                  {isFieldVisible('Description')
                    ? description || t('common.na', 'N/A')
                    : t('grid.amount', 'Amount')}
                </Typography>
                {isFieldVisible('Amount') && (
                  <Typography
                    variant='body1'
                    sx={{
                      flex: '0 0 auto',
                      fontWeight: '600 !important',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {amount}
                  </Typography>
                )}
              </Box>
            )}

            {/* Line 2: Type + Transaction Date */}
            {(isFieldVisible('CategoryName') ||
              isFieldVisible('TransactionDate')) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body2'
                  sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
                >
                  {isFieldVisible('CategoryName')
                    ? typeValue || t('common.na', 'N/A')
                    : ''}
                </Typography>
                {isFieldVisible('TransactionDate') ? (
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    {transactionDateValue}
                  </Typography>
                ) : null}
              </Box>
            )}

            {/* Line 3: Trade Date */}
            {isFieldVisible('TradeDate') && (
              <Line
                left={t('grid.tradeDate', 'Trade Date')}
                right={tradeDateValue}
              />
            )}

            {/* Line 4: Settlement Date */}
            {isFieldVisible('SettlementDate') && (
              <Line
                left={t('grid.settlementDate', 'Settlement Date')}
                right={settlementDateValue}
              />
            )}

            {/* Line 5: Account name */}
            {isFieldVisible('WealthAccountName') && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body2'
                  onClick={
                    accountNameValue && canOpenAccount(row.WealthAccountId)
                      ? () => {
                          openAccountById(row.WealthAccountId);
                        }
                      : undefined
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: 'text.secondary',
                    ...(accountNameValue && canOpenAccount(row.WealthAccountId)
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : {}),
                  }}
                >
                  {accountNameValue || t('common.na', 'N/A')}
                </Typography>
              </Box>
            )}

            {/* Line 6: Ticker */}
            {isFieldVisible('Symbol') && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body2'
                  sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
                >
                  {tickerValue || t('common.na', 'N/A')}
                </Typography>
              </Box>
            )}

            {/* Line 7: CUSIP */}
            {isFieldVisible('CUSIP') && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body2'
                  sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
                >
                  {cusipValue || t('common.na', 'N/A')}
                </Typography>
              </Box>
            )}

            {isFieldVisible('PrincipalCashAmount') && (
              <Line
                left={t('grid.principalCashAmount')}
                right={
                  row.PrincipalCashAmount != null
                    ? formatNumberToCurrency(row.PrincipalCashAmount)
                    : '—'
                }
              />
            )}

            {isFieldVisible('IncomeCashAmount') && (
              <Line
                left={t('grid.incomeCashAmount')}
                right={
                  row.IncomeCashAmount != null
                    ? formatNumberToCurrency(row.IncomeCashAmount)
                    : '—'
                }
              />
            )}
          </Box>
        );
      },
    }),
    [t, canOpenAccount, openAccountById],
  );

  return (
    <ContentContainer
      container={container}
      sx={{
        paddingX: 3,
        paddingTop: 3,
        paddingBottom: 3, // Reduced bottom padding slightly
        gap: 3, // Reduced gap slightly
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%', // Use height: '100%' for flexbox growth
        minHeight: WIDGET_GRID_HEIGHT,
        border: 'none',
        boxShadow: 'none',
      }}
    >
      {/* ContentContainer renders a bare Box (and drops `sx`) for
          container='page' | 'flyout', so the layout that gives the grid its
          height basis has to live on an inner element, not on the container. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          flexGrow: 1,
          height: '100%',
          minHeight: WIDGET_GRID_HEIGHT,
        }}
      >
        <Typography variant='h5'>
          {t('investmentActivity.title', 'Investment Activity')}
        </Typography>
        <Box
          ref={widgetWidthRef}
          sx={{ flex: 1, minHeight: 0, width: '100%' }}
        >
          {error ? (
            <Typography
              color='error'
              sx={{ p: 2 }}
            >
              {t('investmentActivity.loadError')}
            </Typography> // Basic error display
          ) : (
            <GridContainer
              isWidget={isWidget}
              size={container === 'flyout' ? 'flyout' : 'md'}
            >
              <DataGridPremium
                // --- Data & Columns ---
                key={
                  shouldUseListView
                    ? 'investment-activity-xs'
                    : 'investment-activity'
                }
                rows={
                  data?.Records
                    ? data.Records.filter(
                        (row: BrokerageTransactionRow) =>
                          !optimisticallyHiddenRowIds.has(
                            String(row.WealthAccountTransactionId),
                          ),
                      )
                    : []
                }
                sx={(theme) => ({
                  '& .MuiDataGrid-cell': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    py: 1,
                  },
                  ...(shouldUseListView && {
                    // When DataGrid runs column autosizing, it toggles `MuiDataGrid-autosizing` on the root,
                    // which can temporarily apply competing width/minWidth styles. Ensure our list-view
                    // full-width rules always win.
                    '&.MuiDataGrid-autosizing .MuiDataGrid-cell': {
                      width: '100% !important',
                      minWidth: '100% !important',
                      maxWidth: '100% !important',
                    },
                    '&.MuiDataGrid-autosizing .MuiDataGrid-row': {
                      width: '100% !important',
                      minWidth: '100% !important',
                      maxWidth: '100% !important',
                    },
                    // In list view, force the single list column to actually fill the grid width.
                    '& .MuiDataGrid-row:not(.MuiDataGrid-row--group) .MuiDataGrid-cellEmpty':
                      {
                        display: 'none !important',
                      },
                    '& .MuiDataGrid-main': { width: '100%' },
                    '& .MuiDataGrid-virtualScroller': { width: '100%' },
                    '& .MuiDataGrid-virtualScrollerRenderZone': {
                      width: '100% !important',
                      minWidth: '100% !important',
                      maxWidth: '100% !important',
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                      width: '100% !important',
                    },
                    '& .MuiDataGrid-virtualScrollerContent > div': {
                      width: '100% !important',
                      maxWidth: '100% !important',
                    },
                    '& .MuiDataGrid-row': {
                      display: 'flex',
                      flexWrap: 'nowrap',
                      width: '100% !important',
                      minWidth: '100% !important',
                      maxWidth: '100% !important',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      flexWrap: 'nowrap',
                      width: '100% !important',
                      minWidth: '100% !important',
                      maxWidth: '100% !important',
                      flex: '1 1 0',
                    },
                    '& .MuiDataGrid-cellContent': {
                      width: '100%',
                      flex: 1,
                      minWidth: 0,
                    },
                    [theme.breakpoints.down(800)]: {
                      '& .MuiDataGrid-cell': {
                        width: '100% !important',
                        minWidth: '100% !important',
                        display: 'block !important',
                      },
                    },
                  }),
                })}
                columns={columns}
                getRowId={(row) => row.WealthAccountTransactionId} // Use the unique ID from data
                // --- Loading State ---
                loading={isLoading || isFetching}
                // --- API Ref ---
                apiRef={apiRef} // Pass the apiRef to the grid
                // --- Basic Features ---
                disableRowSelectionOnClick
                showToolbar={true}
                disablePivoting
                listView={shouldUseListView}
                {...(shouldUseListView
                  ? { listViewColumn: listViewColDef }
                  : {})}
                {...(shouldUseListView
                  ? { getRowHeight: () => 'auto' as const }
                  : {})}
                // autoHeight // Use if you don't want the grid to fill the container height

                // --- Premium Features (Optional - Requires License) ---
                // disableAggregation={false} // Enable aggregation features
                // disableRowGrouping={false} // Enable row grouping

                // --- Styling & Layout ---
                // sx={{ border: 0 }} // Remove default border if PaperFrameStyled provides one
                pageSizeOptions={[...PAGE_SIZE_OPTIONS.STANDARD]}
                pagination
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: PAGE_SIZES.DEFAULT },
                  },
                }}
                slotProps={{
                  toolbar: {
                    ...defaultToolbarExportSlotProps,
                    showQuickFilter: true,
                  },
                  columnsManagement: { defaultColumnVisibilityModel },
                }}
                slots={{
                  columnsManagement: ManagedColumnsManagement,
                  ...(isNative
                    ? {
                        toolbar: (props: GridToolbarProps) => (
                          <CustomToolbarForNative {...props} />
                        ),
                      }
                    : shouldUseListView
                      ? { toolbar: XsPreferencesDrawerToolbar }
                      : {}),
                }}
              />
            </GridContainer>
          )}
        </Box>
      </Box>
      <EmbeddableMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <MenuItem onClick={handleMoveToCashFlow}>
          {t('transactions.moveToCashFlow', 'Move to Cash Flow')}
        </MenuItem>
      </EmbeddableMenu>
      <MoveToCashFlowModal
        open={moveToCashFlowModalOpen}
        onClose={() => {
          setMoveToCashFlowModalOpen(false);
          setMoveToCashFlowTx(null);
        }}
        transaction={moveToCashFlowTx}
        onDone={() => {
          if (moveToCashFlowTx) {
            setOptimisticallyHiddenRowIds((prev) =>
              new Set(prev).add(
                String(moveToCashFlowTx.WealthAccountTransactionId),
              ),
            );
          }
          setMoveToCashFlowTx(null);
        }}
      />
      {accountFlyout}
    </ContentContainer>
  );
};

export default InvestmentActivity;
